export class ResponseCache {
    loader;
    notifier;
    static MAX_LOAD_RETRY = 2;
    static MAX_CONCURRENT_DOWNLOADS = 6;
    _data = new Map();
    interruptLoading = false;
    _status = new Map();
    constructor(loader, notifier) {
        this.loader = loader;
        this.notifier = notifier;
    }
    status(cacheKey) {
        if (cacheKey) {
            return this._status.get(cacheKey)?.status;
        }
        else {
            return this._status;
        }
    }
    data(cacheKey) {
        if (cacheKey) {
            return this._data.get(cacheKey);
        }
        else {
            return this._data;
        }
    }
    update(keys) {
        const keep = [];
        const promisedData = keys.reduce((cache, entry) => {
            cache.set(entry.cacheKey, { version: entry.version, data: undefined });
            return cache;
        }, new Map());
        //keep all uvFields that have equal or higher version numbers
        Array.from(promisedData.keys()).forEach((cacheKey) => {
            const curValue = this._data.get(cacheKey);
            const promisedValue = promisedData.get(cacheKey);
            if (curValue &&
                curValue.version >= promisedValue.version &&
                curValue.data) {
                promisedValue.data = curValue.data;
                promisedValue.version = curValue.version;
                keep.push(cacheKey);
            }
        });
        const load = Array.from(promisedData.keys()).filter((cacheKey) => promisedData.get(cacheKey).data === undefined);
        const status = load.reduce((status, key) => {
            status.set(key, { key: key, attempts: 0, status: "waiting" });
            return status;
        }, new Map());
        //  this._data = promisedData;
        this.load(status, promisedData);
        return status;
    }
    async load(status, data, parallelCount = ResponseCache.MAX_CONCURRENT_DOWNLOADS) {
        await this.interrupt();
        this._status = status;
        this._data = data;
        return this.startLoading(parallelCount);
    }
    startLoading(parallelCount) {
        return new Promise((resolve, reject) => {
            const entriesToLoad = Array.from(this._status.values()).filter((statusEntry) => statusEntry.status === "waiting");
            let startCount = Math.min(entriesToLoad.length, Math.max(parallelCount, 1));
            if (startCount > 0) {
                let loadOperations = [];
                let i = 0;
                while (i < startCount) {
                    loadOperations.push(this.getLoadOperation());
                    i++;
                }
                Promise.all(loadOperations).then(() => {
                    if (this.notifier) {
                        this.notifier(); //signals all done
                    }
                    resolve();
                });
            }
            else {
                if (this.notifier) {
                    this.notifier(); //signals all done
                }
                resolve();
            }
        });
    }
    getLoadOperation() {
        const loadValue = (cacheKey) => this.loader(cacheKey)
            .then((cacheData) => this.processLoadResult({ cacheKey, cacheData }))
            .catch((error) => this.processLoadError({ cacheKey, error }));
        return this.loadWorker(loadValue);
    }
    processLoadError(loadError) {
        const progressEntry = this._status.get(loadError.cacheKey);
        if (progressEntry) {
            progressEntry.status =
                loadError.error.status === 404 ? "no-key" : "error";
            progressEntry.attempts++;
            if (!this.interruptLoading &&
                progressEntry.status === "error" &&
                progressEntry.attempts < ResponseCache.MAX_LOAD_RETRY) {
                progressEntry.status = "waiting";
            }
            return loadError.cacheKey;
        }
        else {
            console.log(`Cache key ${JSON.stringify(loadError.cacheKey)} could not be found while processing loading error.`);
            return undefined;
        }
    }
    processLoadResult(loadResult) {
        const progressEntry = this._status.get(loadResult.cacheKey);
        const cachValue = this._data.get(loadResult.cacheKey);
        if (cachValue) {
            if (progressEntry) {
                progressEntry.status = loadResult.cacheData ? "ok" : "no-content";
                cachValue.data = loadResult.cacheData;
                progressEntry.attempts++;
                return loadResult.cacheKey;
            }
            else {
                console.log(`Cache key ${JSON.stringify(loadResult.cacheKey)} could not be found while processing loading result.`);
                return undefined;
            }
        }
        else {
            console.log(`Cache key ${JSON.stringify(loadResult.cacheKey)} did not produce a cache value while processing loading result.`);
            return undefined;
        }
    }
    loadWorker(loadValue) {
        return new Promise(async (resolve, reject) => {
            const keysToLoad = Array.from(this._status.values()).filter((keyToLoad) => keyToLoad.status === "waiting");
            if (keysToLoad.length > 0) {
                //take first
                keysToLoad[0].status = "running"; //change status to indicate we are loading this one
                loadValue(keysToLoad[0].key)
                    .then((cacheKey) => {
                    if (cacheKey) {
                        if (this.notifier) {
                            this.notifier(cacheKey);
                        }
                    }
                    else {
                        //cacheKey did not yield progress or cache entries (which should not happen)
                        console.log("cacheKey did not yield progress or cache entries (which should not happen)");
                    }
                })
                    .then(() => {
                    if (this.interruptLoading) {
                        //the interrupt flag has been raised, remove all keys that still need loading but are not actively being loaded now
                        const waiting = Array.from(this._status.values()).filter((keyToLoad) => keyToLoad.status === "waiting");
                        if (waiting.length > 0) {
                            waiting.forEach((keyToLoad) => {
                                keyToLoad.status = "interrupted";
                            });
                            console.log(`Cancelled ${waiting.length} waiting load requests. Loading interrupted.`);
                        }
                        resolve();
                    }
                    else {
                        const keysToLoad = Array.from(this._status.values()).filter((keyToLoad) => keyToLoad.status === "waiting");
                        resolve(keysToLoad.length > 0 ? this.loadWorker(loadValue) : undefined);
                    }
                });
            }
        });
    }
    interrupt() {
        const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const loadingHalted = () => {
            const keysNotFinished = Array.from(this._status.values()).filter((keyToLoad) => keyToLoad.status === "waiting" || keyToLoad.status === "running");
            if (keysNotFinished.length === 0) {
                this.interruptLoading = false;
                return true;
            }
            else {
                return false;
            }
        };
        const waitForLoadingToHalt = (actualInterrupt) => {
            return loadingHalted()
                ? Promise.resolve(actualInterrupt)
                : wait(5).then(() => waitForLoadingToHalt(true));
        };
        this.interruptLoading = true;
        return waitForLoadingToHalt(false);
    }
}
